import fetchUsers, { fetchAccounts, fetchFilterOptions } from './usersService';

const USERS_BODY = {
  users: [{ id: 1, name: 'Abe Martinez' }],
  pagination: {
    page: 2,
    per_page: 25,
    total_entries: 95,
    total_pages: 4,
  },
};

const respondWith = (body) => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok: true, json: async () => body });
};

const requestedUrl = () => global.fetch.mock.calls[0][0];

afterEach(() => {
  delete global.fetch;
});

describe('management/Users usersService', () => {
  describe('fetchUsers', () => {
    beforeEach(() => respondWith(USERS_BODY));

    it('returns the rows untouched', async () => {
      const { users } = await fetchUsers();

      expect(users).toEqual(USERS_BODY.users);
    });

    // The wire format is snake_case; the components read camelCase.
    it('renames the pagination fields', async () => {
      const { pagination } = await fetchUsers();

      expect(pagination).toEqual({
        page: 2,
        perPage: 25,
        totalItems: 95,
        totalPages: 4,
      });
    });

    it('asks for JSON', async () => {
      await fetchUsers();

      expect(global.fetch.mock.calls[0][1]).toEqual({
        headers: { Accept: 'application/json' },
      });
    });

    it('defaults to the first page', async () => {
      await fetchUsers();

      expect(requestedUrl()).toEqual('/management/users.json?page=1');
    });

    it('sends the requested page', async () => {
      await fetchUsers({ page: 3 });

      expect(requestedUrl()).toContain('page=3');
    });

    it('omits the sort when no field is given', async () => {
      await fetchUsers({ sortOrder: 'DESC' });

      expect(requestedUrl()).not.toContain('sort_');
    });

    it('sends the sort field and direction', async () => {
      await fetchUsers({ sortBy: 'job_role', sortOrder: 'DESC' });

      expect(requestedUrl()).toContain('sort_by=job_role&sort_order=DESC');
    });

    it('defaults the direction when a field has none', async () => {
      await fetchUsers({ sortBy: 'job_role' });

      expect(requestedUrl()).toContain('sort_order=ASC');
    });

    it('omits the search term when empty', async () => {
      await fetchUsers({ query: '' });

      expect(requestedUrl()).not.toContain('q=');
    });

    it('sends the search term', async () => {
      await fetchUsers({ query: 'martinez' });

      expect(requestedUrl()).toContain('q=martinez');
    });

    // Rails reads these as arrays, so each value repeats the bracketed key.
    it('repeats each filter value under a bracketed key', async () => {
      await fetchUsers({
        filters: {
          accountIds: [1, 2],
          permissions: ['Member'],
          jobRoles: ['technician'],
        },
      });

      expect(decodeURIComponent(requestedUrl())).toContain(
        'account_ids[]=1&account_ids[]=2&permissions[]=Member&job_roles[]=technician',
      );
    });

    it('omits filters that are absent', async () => {
      await fetchUsers({ filters: {} });

      expect(requestedUrl()).not.toContain('account_ids');
    });

    it('throws on a non-OK response, naming the status', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

      await expect(fetchUsers()).rejects.toThrow('status: 500');
    });
  });

  describe('fetchFilterOptions', () => {
    beforeEach(() =>
      respondWith({
        permissions: ['Member', 'Manager'],
        jobRoles: [{ label: 'Technician', value: 'technician' }],
      }),
    );

    // Role names are their own value, unlike job roles which arrive paired.
    it('pairs each role name as its own label and value', async () => {
      const { permissions } = await fetchFilterOptions();

      expect(permissions).toEqual([
        { label: 'Member', value: 'Member' },
        { label: 'Manager', value: 'Manager' },
      ]);
    });

    it('passes job roles through as given', async () => {
      const { jobRoles } = await fetchFilterOptions();

      expect(jobRoles).toEqual([{ label: 'Technician', value: 'technician' }]);
    });

    it('throws on a non-OK response', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 403 });

      await expect(fetchFilterOptions()).rejects.toThrow('status: 403');
    });
  });

  describe('fetchAccounts', () => {
    beforeEach(() =>
      respondWith({ accounts: [{ label: 'Alpha', value: 1 }], total: 40 }),
    );

    it('returns the page and the total', async () => {
      expect(await fetchAccounts()).toEqual({
        accounts: [{ label: 'Alpha', value: 1 }],
        total: 40,
      });
    });

    // The menu asks for the next page as it is scrolled (PD-4577).
    it('sends the offset', async () => {
      await fetchAccounts('', 25);

      expect(requestedUrl()).toEqual(
        '/management/users/accounts.json?offset=25',
      );
    });

    // The first page is offset 0, which is the default; sending it would only
    // make the URL noisier.
    it('omits a zero offset', async () => {
      await fetchAccounts('alpha', 0);

      expect(requestedUrl()).toEqual('/management/users/accounts.json?q=alpha');
    });

    // Reporting zero would read as "no accounts" and hide the loaded page.
    it('falls back to the page size when the response carries no total', async () => {
      respondWith({ accounts: [{ label: 'Alpha', value: 1 }] });

      expect(await fetchAccounts()).toEqual({
        accounts: [{ label: 'Alpha', value: 1 }],
        total: 1,
      });
    });

    it('returns an empty page when the response carries no accounts', async () => {
      respondWith({});

      expect(await fetchAccounts()).toEqual({ accounts: [], total: 0 });
    });

    // The menu opens before anything is typed.
    it('sends no query string when there is no term', async () => {
      await fetchAccounts();

      expect(requestedUrl()).toEqual('/management/users/accounts.json');
    });

    it('sends the search term', async () => {
      await fetchAccounts('alpha');

      expect(requestedUrl()).toEqual('/management/users/accounts.json?q=alpha');
    });

    it('escapes a term with characters that need it', async () => {
      await fetchAccounts('a & b');

      expect(requestedUrl()).toContain('q=a+%26+b');
    });

    it('throws on a non-OK response', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

      await expect(fetchAccounts('x')).rejects.toThrow('status: 500');
    });
  });
});
